<template>
  <v-dialog
    v-model="$store.state.appointmentsCalendarStore.appointmentsSelectedDayPage.rescheduleAppointmentDialog"
    width="600"
    persistent
    scrollable
  >
    <v-card class="hoh-entry-form">
      <v-card-title class="headline primary white--text" primary-title>
        Reschedule Appointment
        <v-spacer></v-spacer>
        <v-btn @click="closeModal()" left text fab small color="white"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert type="info" text class="mt-4 mb-8 mx-auto font-bold">
          Choose a desired date to reschedule and then proceed to select a new time slot.
        </v-alert>

        <!-- SELECT DATE -->
        <v-menu v-model="dateMenuOpen" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" min-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="computedDate"
              label="Start Date"
              readonly
              outlined
              :disabled="loadingSlots"
              dense
              class="required"
              :rules="[(v) => !!v || 'This field is required']"
              append-icon="mdi-calendar"
              @click:append="dateMenuOpen = true"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            :min="today"
            show-week
            @change="changeDate($event)"
            @input="dateMenuOpen = false"
            :show-current="false"
            v-model="date"
          ></v-date-picker>
        </v-menu>

        <!-- SELECT LOCATION -->
        <v-select
          class="mb-4"
          v-if="locations"
          v-model="newLocationSelected"
          :hint="`${newLocationSelected.address}, ${newLocationSelected.id}`"
          :items="locations"
          item-text="location"
          item-value="id"
          label="Select"
          outlined
          persistent-hint
          @change="changeLocation($event)"
          return-object
          append-icon="mdi-domain"
          dense
        >
          <template v-slot:item="data">
            <template>
              <v-list-item-content>
                <v-list-item-title v-html="data.item.location"></v-list-item-title>
                <v-list-item-subtitle v-html="data.item.address"></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </template>
        </v-select>

        <v-expansion-panels multiple v-if="!loadingSlots" class="mb-4">
          <v-expansion-panel v-for="(item, i) in timeSlotsGroupedByDate" :key="i">
            <v-expansion-panel-header>
              <div>
                {{ formatDateTime(i) }}
              </div>
              <v-spacer />
              <v-avatar size="20" color="success" style="flex: none" class="white--text me-2" v-if="getSlotTypeCount(item, 'AVAILABLE') > 0">
                {{ getSlotTypeCount(item, 'AVAILABLE') }}
              </v-avatar>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="ma-0 pa-0">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="">Duration</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="slot in item" :key="slot.id">
                      <td>{{ slot.duration }} minutes</td>
                      <td style="text-align: right">
                        <v-btn
                          small
                          class="px-5"
                          color="success"
                          v-if="slot.state == 'AVAILABLE'"
                          @click="selectSlot(slot)"
                          :disabled="slot.selected"
                        >
                          <span v-if="slot.selected"> selected </span>
                          <span v-else>Select</span>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="pa-8 text-center title" v-if="!loadingSlots && !hasSlots">No open slots on this date at this location</div>
        <div class="mx-auto text-center" v-if="loadingSlots">
          <v-progress-circular :size="70" :width="7" color="primary" class="mx-auto" indeterminate></v-progress-circular>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="closeModal()"> Cancel </v-btn>
        <v-btn
          color="success"
          :disabled="submittingForm || (!loadingSlots && !hasSlots) || !selectedSlotId"
          :loading="submittingForm"
          @click="submit()"
          class="mx-4"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
export default {
  data() {
    return {
      timeSlotsGroupedByDate: {},
      timeSlotsArray: [],
      loadingSlots: false,
      date: null,
      dateMenuOpen: false,
      selectedSlotId: null,
      submittingForm: false,
      currentSelectedSlotId: null,
      newLocationSelected: null,
      locations: []
    };
  },
  components: {},
  computed: {
    today() {
      return moment().format('YYYY-MM-DD');
    },
    hasSlots() {
      if (Object.keys(this.timeSlotsGroupedByDate).length > 0) return true;
      else return false;
    },
    selectedDate() {
      return this.$store.state.appointmentsCalendarStore.appointmentsSelectedDayPage.selectedDate;
    },
    computedDate() {
      return this.date ? moment(this.date, 'YYYY-MM-DD').format('D-MMMM-YYYY') : '';
    },
    locationSelected() {
      return this.$store.state.appointmentsCalendarStore.locationSelected;
    },
    locationOptions() {
      return this.$store.state.appointmentsCalendarStore.locationOptions;
    }
  },
  created() {
    this.date = this.selectedDate;
    this.currentSelectedSlotId = this.$store.state.appointmentsCalendarStore.appointmentsSelectedDayPage.currentSelectedSlotId;
    this.populateLocationOptions();
    this.getEntriesOnDate(this.selectedDate);
  },
  methods: {
    submit() {
      this.submittingForm = true;
      this.$store
        .dispatch('appointmentsCalendarStore/rescheduleAppoiment', {
          currentAgendaSlotId: this.currentSelectedSlotId,
          newAgendaSlotId: this.selectedSlotId,
          location: this.newLocationSelected.id
        })
        .then((data) => {
          if (data) {
            this.submittingForm = false;
            this.$store.commit('appointmentsCalendarStore/SET_RESCHEDULE_APPOINTMENT_DIALOG', false);
            let selectedDate = this.$store.state.appointmentsCalendarStore.appointmentsSelectedDayPage.selectedDate;
            this.$store.dispatch('appointmentsCalendarStore/getCalenderDayManualAppointments', {
              minDate: selectedDate,
              maxDate: selectedDate,
              locationId: this.locationSelected.id
            });
            this.$store.dispatch('appointmentsCalendarStore/getCalenderDaySlots', {
              minDate: selectedDate,
              maxDate: selectedDate,
              locationId: this.locationSelected.id
            });
            let payload = {
              open: true,
              text: 'Appointment successfully rescheduled',
              color: 'success'
            };
            this.$store.commit('SET_SNACKBAR', payload);
          }
        });
    },
    selectSlot(slot) {
      this.timeSlotsArray.forEach((item) => {
        if (slot.id == item.id) {
          this.selectedSlotId = item.id;
          item.selected = true;
        } else item.selected = false;
      });

      this.timeSlotsGroupedByDate = this.groupByTimeSlot(this.timeSlotsArray);
    },
    async getEntriesOnDate(day) {
      this.loadingSlots = true;
      let timeSlots = await this.$store.dispatch('appointmentsCalendarStore/getCalenderDaySlotsForRescheduling', {
        minDate: day,
        maxDate: day,
        locationId: this.newLocationSelected.id
      });
      this.timeSlotsArray = timeSlots.map((item) => {
        return { ...item, selected: false };
      });
      this.loadingSlots = false;
      this.timeSlotsGroupedByDate = this.groupByTimeSlot(this.timeSlotsArray);
    },
    formatDateTime(d) {
      return moment.utc(d, 'YYYY-MM-DDTHH:mm').local().format('hh:mm A');
    },
    closeModal() {
      this.$store.commit('appointmentsCalendarStore/SET_RESCHEDULE_APPOINTMENT_DIALOG', false);
    },
    groupByTimeSlot(data) {
      let result = data
        .filter((item) => item.state == 'AVAILABLE')
        .reduce(function (r, a) {
          r[a.date] = r[a.date] || [];
          r[a.date].push(a);
          return r;
        }, Object.create(null));

      return result;
    },
    getSlotTypeCount(slots, type) {
      if (!slots) return;
      let items = slots.filter((slot) => {
        return slot.state == type;
      });
      return items.length;
    },
    changeDate(value) {
      this.selectedSlotId = null;
      this.date = value;
      this.getEntriesOnDate(value);
    },
    populateLocationOptions() {
      this.locations = this.locationOptions;
      this.newLocationSelected = this.locationSelected;
    },
    changeLocation() {
      this.getEntriesOnDate(this.date);
    }
  }
};
</script>
